import React, { useMemo } from 'react';
import I18n from 'i18n-js';
import { debounce } from 'lodash';

import { PriceDisplay } from '@reverbdotcom/commons/src/components/PriceDisplay';
import { RCTooltip } from '@reverbdotcom/commons';
import { CA } from '@reverbdotcom/commons/src/constants';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { RCAlertBox, RCIcon } from '@reverbdotcom/cadence/components';
import { MParticleEventName, trackEvent, EventType } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { InfoIcon } from '@phosphor-icons/react';
import { ReverbLogoCircleIcon } from '@reverbdotcom/cadence/icons/react';
import { Shop } from '@reverbdotcom/commons/src/gql/graphql';
import { filterVatCountries } from '../../../shop_settings_vat_layout';
import StringUtil from '../../../../reverb-js-common/string_util';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { useEstimatedEarnings } from './useEstimatedEarnings';

const TOOLTIP_EVENT_DELAY = 3000;
const VAT_ID_HC_ARTICLE = '15478546545171';
const COMPONENT_NAME = 'PriceBreakdown';

const SELLING_FEE = 'SELLING_FEE';
const PAYMENT_PROCESSING_FEE = 'PAYMENT_PROCESSING_FEE';
const WALLET_INCENTIVE = 'WALLET_INCENTIVE';
const ESTIMATED_EARNINGS = 'ESTIMATED_EARNINGS';

const priceKeys = {
  [SELLING_FEE]: 'sellingFee',
  [PAYMENT_PROCESSING_FEE]: 'processingFee',
  [WALLET_INCENTIVE]: 'walletIncentive',
  [ESTIMATED_EARNINGS]: 'estimatedEarnings',
};

function isVatCountry(shop: Shop) {
  const address = shop?.address ?? {};

  return !!filterVatCountries([{
    countryCode: address.countryCode,
  }]).length || address.countryCode === CA;
}

function formatTooltipText(text: string | undefined): React.ReactNode {
  if (!text) return null;
  return <span style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>{text}</span>;
}

function toolTipOpenEvent(open: boolean, tooltipName: string) {
  if (!open) return;

  trackEvent({
    eventName: MParticleEventName.TooltipOpened,
    eventType: EventType.Other,
    componentName: COMPONENT_NAME,
    source: tooltipName,
  });
}

export function PriceBreakdown({
  price,
  currency,
  listingId = undefined,
  payoutToWallet = undefined,
  shop = {},
  showIncVatPricingHint = false,
}: {
  price: string;
  currency: string;
  listingId?: string;
  payoutToWallet?: boolean;
  shop?: Pick<Shop, 'address' | 'vatId'>;
  showIncVatPricingHint?: boolean;
}) {
  const { data, previousData, loading, errors } = useEstimatedEarnings({ listingId, price, currency, payoutToWallet });
  // Keep showing the last estimate while a new one loads, so the table doesn't flash on every price change
  const estimatedEarningsResponse = loading ? (data ?? previousData) : data;

  const vatIdHcArticleUrl = useHelpCenterArticleUrl(VAT_ID_HC_ARTICLE);
  const debouncedTooltipOpenEvent = useMemo(() => debounce(toolTipOpenEvent, TOOLTIP_EVENT_DELAY), []);

  const {
    amountCents: estimatedEarningsAmountCents,
    display: estimatedEarningsDisplayAmount,
  } = estimatedEarningsResponse?.estimatedEarnings?.estimatedEarnings || {};

  if (errors || (estimatedEarningsAmountCents ?? 0) <= 0) return null;

  const taxIncludedHint = showIncVatPricingHint ? I18n.t('discovery.sellForm.pricingSection.priceBreakdown.estimatedEarnings.specifier') : '';
  const showVatIdHcArticle = isVatCountry(shop) && !shop?.vatId;

  const estimatedEarningsLineItem = {
    type: ESTIMATED_EARNINGS,
    tooltipText: estimatedEarningsResponse?.estimatedEarnings?.estimatedEarningsTooltipText,
    fee: {
      display: estimatedEarningsDisplayAmount,
      amountCents: estimatedEarningsAmountCents,
    },
    label: I18n.t('discovery.sellForm.pricingSection.priceBreakdown.estimatedEarnings.label', { specifier: taxIncludedHint }),
  };

  const feeLineItems = [ ...(estimatedEarningsResponse?.estimatedEarnings?.feeLineItems || []), estimatedEarningsLineItem ];

  return (
    <>
      <table className="price-breakdown__table">
        <tbody>
          {feeLineItems.map(({ type, label, tooltipText, fee: { display, amountCents } }) => {
            if (amountCents === 0) return null;

            return (
              <tr key={type} id={StringUtil.camelCaseToKebabCase(priceKeys[type])}>
                <td>
                  <RCTooltip
                    text={formatTooltipText(tooltipText)}
                    onOpenChange={(open) => debouncedTooltipOpenEvent(open, priceKeys[type])}
                  >
                    <div className="d-flex fx-align-center">
                      {type === WALLET_INCENTIVE && <ReverbLogoCircleIcon className="reverb-logo-circle-icon" />}
                      {label}
                      {type !== WALLET_INCENTIVE && <div className="ml-1"><RCIcon icon={<InfoIcon />} size="inline" /></div>}
                    </div>
                  </RCTooltip>
                </td>
                <td className="price-breakdown__value">
                  <PriceDisplay
                    display={display}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {showVatIdHcArticle &&
        <div className="price-breakdown__vat-id-alert">
          <RCAlertBox type="warning" small>
            <I18N
              html
              text="discovery.sellForm.pricingSection.priceBreakdown.vatIdAlert"
              args={{ linkOpen: `<br /><a href="${vatIdHcArticleUrl}">`, linkClose: '</a>' }}
            />
          </RCAlertBox>
        </div>
      }
    </>
  );
}
